import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { API_BASE } from '../config/api'

const INACTIVITY_TIMEOUT = 900000; // 15 minutes
const SESSION_CHECK_INTERVAL = 5000;

const SessionTimedOut = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef();
  const socketRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Skip login pages entirely
    if (location.pathname === "/" || location.pathname === "/staff-login") {
      return;
    }

    // Logs out ONLY this tab/session — no cross-tab broadcast
    const logout = () => {
      const adminToken = sessionStorage.getItem("adminToken");
      const subAdminToken = sessionStorage.getItem("subAdminToken");

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      clearInterval(checkIntervalRef.current);
      clearTimeout(timeoutRef.current);

      if (adminToken) {
        sessionStorage.removeItem("adminToken");
        sessionStorage.removeItem("adminData");
        navigate("/", { replace: true });
        return;
      }

      if (subAdminToken) {
        sessionStorage.removeItem("subAdminToken");
        sessionStorage.removeItem("subAdminData");
        navigate("/staff-login", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    };

    // Reset inactivity timer on user activity
    const resetTimer = () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
    };

    const adminToken = sessionStorage.getItem("adminToken");
    const subAdminToken = sessionStorage.getItem("subAdminToken");
    const token = adminToken || subAdminToken;

    if (!token) {
      logout();
      return;
    }

    const adminData = sessionStorage.getItem("adminData");
    const subAdminAuth = sessionStorage.getItem("subAdminData");

    const sessionId = adminToken
      ? adminData
        ? JSON.parse(adminData).activeSessionId
        : null
      : subAdminAuth
      ? JSON.parse(subAdminAuth).activeSessionId
      : null;

      console.log("session Id :", sessionId)

    // Pick the right live-session check event depending on who's logged in
    const checkEventName = adminToken
      ? "check-super-admin-session"
      : "check-staff-session";

    // --- Socket.IO live session check (admin or staff) ---
    if (sessionId) {
      const socket = io(API_BASE, {
        transports: ["websocket"],
        // Browsers can't set custom headers on a raw WS handshake,
        // so send the token via the socket.io `auth` payload instead
        // of an `authorization` header (matches EIO=4 handshake).
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        console.log(
          `Session socket connected (${checkEventName}):`,
          sessionId
        );
      });

      socket.on("connect_error", (err) => {
        console.error("Session socket connection error:", err.message);
      });

      const checkSession = () => {
        if (!socket.connected) return;

        socket.emit(checkEventName, { sessionId }, (response) => {
          console.log("Session check response:", response);

          // Case 1: session is active -> stay logged in, do nothing further
          if (response && response.success === true && response.isActive === true) {
            return;
          }

          // Case 2: session is no longer active -> log out THIS session only
          // Covers: { success: false, isActive: false, ... } and any
          // malformed/missing response, treated as "not active".
          if (!response || response.success === false || response.isActive === false) {
            logout();
          }
        });
      };

      // Check immediately, then on an interval
      checkSession();
      checkIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);

      // Optional: if the server proactively pushes a termination event
      // for this specific session
      socket.on("session-terminated", () => {
        logout();
      });
    } else {
      console.warn("No active session id found; skipping live session check.");
    }

    // --- Activity listeners for inactivity timeout ---
    ["mousemove", "keydown", "click", "scroll"].forEach((event) =>
      window.addEventListener(event, resetTimer)
    );
    resetTimer();

    return () => {
      clearTimeout(timeoutRef.current);
      clearInterval(checkIntervalRef.current);

      ["mousemove", "keydown", "click", "scroll"].forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [navigate, location.pathname]);

  return null;
};

export default SessionTimedOut;